import { io } from "socket.io-client";

/* ===============================
   NORMALIZE BASE
================================ */
const normalizeBase = (value) =>
  String(value || "").trim().replace(/\/+$/, "");

/* ===============================
   BASE URL
================================ */
const ensureApiBase = (value) => {
  const clean = normalizeBase(value);

  if (!clean) return "http://localhost:5000/api";

  return clean.endsWith("/api") ? clean : `${clean}/api`;
};

const API_BASE = ensureApiBase(
  process.env.REACT_APP_API_BASE || "http://localhost:5000"
);

const SOCKET_BASE = API_BASE.endsWith("/api")
  ? API_BASE.replace(/\/api$/, "")
  : API_BASE;

/* ===============================
   AUTH KEYS
================================ */
const AUTH_TOKEN_KEYS = [
  "token",
  "authToken",
  "accessToken",
  "userToken",
  "jwtToken",
];

const AUTH_STORAGE_KEYS = [
  "token",
  "authToken",
  "accessToken",
  "userToken",
  "jwtToken",
  "isLoggedIn",
  "user",
  "currentUser",
  "name",
  "userName",
  "fullName",
  "username",
  "email",
  "userEmail",
  "user_email",
  "demoUserEmail",
];

/* ===============================
   SOCKET STATE
================================ */
let socketInstance = null;
let isConnecting = false;
let activeToken = "";
let authClearInProgress = false;
let lastConnectAttemptAt = 0;

const forwarderHandlers = new Map();

/* ===============================
   SAFE STORAGE
================================ */
const safeLocalGet = (key) => {
  try {
    return localStorage.getItem(key) || "";
  } catch {
    return "";
  }
};

const safeSessionGet = (key) => {
  try {
    return sessionStorage.getItem(key) || "";
  } catch {
    return "";
  }
};

const safeLocalRemove = (key) => {
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
};

const safeSessionRemove = (key) => {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // ignore
  }
};

/* ===============================
   SAFE EVENTS
================================ */
const dispatchAuthEvent = (action, extra = {}) => {
  try {
    window.dispatchEvent(
      new CustomEvent("auth-state-changed", {
        detail: {
          source: "realtimeSocket",
          action,
          ...extra,
        },
      })
    );
  } catch {
    // ignore
  }
};

const dispatchSocketEvent = (action, extra = {}) => {
  try {
    window.dispatchEvent(
      new CustomEvent("realtime-socket-state", {
        detail: {
          source: "realtimeSocket",
          action,
          connected: Boolean(socketInstance?.connected),
          connecting: isConnecting,
          socketId: socketInstance?.id || null,
          ...extra,
        },
      })
    );
  } catch {
    // ignore
  }
};

const dispatchAppRealtimeEvent = (eventName, payload = {}) => {
  try {
    window.dispatchEvent(
      new CustomEvent("app-realtime-update", {
        detail: {
          source: "realtimeSocket",
          eventName,
          payload,
          time: new Date().toISOString(),
        },
      })
    );
  } catch {
    // ignore
  }
};

/* ===============================
   JWT HELPERS
================================ */
const decodeJwtPayload = (token) => {
  try {
    if (!token || !token.includes(".")) return null;

    const base64Url = token.split(".")[1];

    if (!base64Url) return null;

    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");

    const paddedBase64 = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      "="
    );

    const jsonPayload = decodeURIComponent(
      atob(paddedBase64)
        .split("")
        .map((char) => {
          return `%${`00${char.charCodeAt(0).toString(16)}`.slice(-2)}`;
        })
        .join("")
    );

    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
};

const isTokenValid = (token) => {
  const payload = decodeJwtPayload(token);

  if (!payload) return false;

  if (!payload.exp) return true;

  return payload.exp * 1000 > Date.now();
};

const getTokenUserKey = (token = "") => {
  const payload = decodeJwtPayload(token);

  if (!payload) return "";

  return String(
    payload.id ||
      payload.userId ||
      payload.user_id ||
      payload.email ||
      payload.uid ||
      ""
  ).trim();
};

/* ===============================
   GET TOKEN SAFE
================================ */
const getToken = () => {
  for (const key of AUTH_TOKEN_KEYS) {
    const localValue = safeLocalGet(key);
    const sessionValue = safeSessionGet(key);

    if (localValue && String(localValue).trim()) {
      return String(localValue).trim();
    }

    if (sessionValue && String(sessionValue).trim()) {
      return String(sessionValue).trim();
    }
  }

  return "";
};

/* ===============================
   CLEAR AUTH SAFE
================================ */
const clearAuthStorage = () => {
  if (authClearInProgress) return;

  authClearInProgress = true;

  AUTH_STORAGE_KEYS.forEach((key) => {
    safeLocalRemove(key);
    safeSessionRemove(key);
  });

  dispatchAuthEvent("clear-auth");

  setTimeout(() => {
    authClearInProgress = false;
  }, 500);
};

/* ===============================
   AUTH ERROR CHECK
================================ */
const isAuthSocketError = (error) => {
  const message = String(error?.message || error || "").toLowerCase();

  return (
    message.includes("unauthorized") ||
    message.includes("authentication") ||
    message.includes("jwt") ||
    message.includes("token") ||
    message.includes("forbidden") ||
    message.includes("invalid signature") ||
    message.includes("session expired")
  );
};

/* ===============================
   APP REALTIME EVENTS
================================ */
const APP_REFRESH_EVENTS = [
  "socket:connected",

  "dashboard:update",

  "domain:status",
  "domain:update",
  "domain:purchased",

  "dns:update",
  "dns:zone_created",
  "dns:record_created",
  "dns:record_deleted",

  "hosting:status",
  "hosting:setup",
  "hosting:setup_updated",

  "hosting_panel:project_update",
  "hosting_panel:project_created",
  "hosting_panel:project_deleted",
  "hosting_panel:log",

  "payment:success",
  "payment:update",
  "payment:status",

  "notification:new",
  "notification:update",
  "activity:new",
  "activity:update",

  "wallet:update",
  "wallet:status",
  "favorite:update",
  "favorites:update",
  "order:update",
  "order:new",
];

const attachAppRealtimeForwarders = (socket) => {
  if (!socket || socket.__appForwardersAttached) return;

  socket.__appForwardersAttached = true;

  APP_REFRESH_EVENTS.forEach((eventName) => {
    const handler = (payload = {}) => {
      dispatchAppRealtimeEvent(eventName, payload);
    };

    forwarderHandlers.set(eventName, handler);
    socket.on(eventName, handler);
  });
};

const detachAppRealtimeForwarders = (socket) => {
  if (!socket) return;

  forwarderHandlers.forEach((handler, eventName) => {
    socket.off(eventName, handler);
  });

  forwarderHandlers.clear();
  socket.__appForwardersAttached = false;
};

/* ===============================
   INTERNAL SOCKET EVENTS
================================ */
const attachInternalSocketEvents = (socket) => {
  if (!socket || socket.__internalEventsAttached) return;

  socket.__internalEventsAttached = true;

  socket.on("connect", () => {
    isConnecting = false;

    dispatchSocketEvent("connected", {
      socketId: socket.id,
    });
  });

  socket.on("disconnect", (reason) => {
    isConnecting = false;

    dispatchSocketEvent("disconnected", {
      reason,
    });
  });

  socket.on("connect_error", (err) => {
    isConnecting = false;

    dispatchSocketEvent("connect-error", {
      message: err?.message || "Socket connection error",
    });

    if (isAuthSocketError(err)) {
      disconnectRealtimeSocket({
        clearListeners: true,
        clearAuth: true,
      });
    }
  });

  socket.io.on("reconnect_attempt", () => {
    const latestToken = getToken();

    if (!latestToken || !isTokenValid(latestToken)) {
      disconnectRealtimeSocket({
        clearListeners: true,
        clearAuth: true,
      });

      return;
    }

    activeToken = latestToken;

    socket.auth = {
      token: latestToken,
      userKey: getTokenUserKey(latestToken),
    };

    dispatchSocketEvent("reconnect-attempt");
  });

  socket.io.on("reconnect", () => {
    isConnecting = false;
    dispatchSocketEvent("reconnected");
  });

  socket.io.on("reconnect_failed", () => {
    isConnecting = false;
    dispatchSocketEvent("reconnect-failed");
  });
};

/* ===============================
   SAFE SOCKET CONNECT
================================ */
export const connectRealtimeSocket = () => {
  const token = getToken();

  if (!token || !isTokenValid(token)) {
    disconnectRealtimeSocket({
      clearListeners: true,
      clearAuth: Boolean(token),
    });

    return null;
  }

  const now = Date.now();

  if (isConnecting && socketInstance && now - lastConnectAttemptAt < 800) {
    return socketInstance;
  }

  if (socketInstance && activeToken === token) {
    if (!socketInstance.connected && !isConnecting) {
      isConnecting = true;
      lastConnectAttemptAt = now;

      socketInstance.auth = {
        token,
        userKey: getTokenUserKey(token),
      };

      socketInstance.connect();

      dispatchSocketEvent("connecting-existing");
    }

    attachInternalSocketEvents(socketInstance);
    attachAppRealtimeForwarders(socketInstance);

    return socketInstance;
  }

  if (socketInstance && activeToken !== token) {
    detachAppRealtimeForwarders(socketInstance);
    socketInstance.removeAllListeners();
    socketInstance.disconnect();
    socketInstance = null;
    isConnecting = false;
  }

  activeToken = token;
  isConnecting = true;
  lastConnectAttemptAt = now;

  socketInstance = io(SOCKET_BASE, {
    auth: {
      token,
      userKey: getTokenUserKey(token),
    },

    transports: ["websocket", "polling"],
    withCredentials: true,

    reconnection: true,
    reconnectionAttempts: 10,
    reconnectionDelay: 1200,
    reconnectionDelayMax: 8000,
    randomizationFactor: 0.5,

    timeout: 15000,
    autoConnect: true,
    forceNew: false,

    upgrade: true,
    rememberUpgrade: false,
  });

  attachInternalSocketEvents(socketInstance);
  attachAppRealtimeForwarders(socketInstance);

  dispatchSocketEvent("connecting-new");

  return socketInstance;
};

/* ===============================
   SAFE DISCONNECT
================================ */
export const disconnectRealtimeSocket = (options = {}) => {
  const { clearListeners = true, clearAuth = false } = options;

  if (socketInstance) {
    detachAppRealtimeForwarders(socketInstance);

    if (clearListeners) {
      socketInstance.removeAllListeners();
    }

    socketInstance.disconnect();
    socketInstance = null;
  }

  activeToken = "";
  isConnecting = false;
  lastConnectAttemptAt = 0;

  dispatchSocketEvent("manual-disconnect");

  if (clearAuth) {
    clearAuthStorage();
  }
};

/* ===============================
   COMPONENT SAFE CLEANUP
================================ */
export const cleanupRealtimeSocketListeners = (events = []) => {
  if (!socketInstance || !Array.isArray(events)) return;

  events.forEach((item) => {
    if (!item?.event || !item?.handler) return;
    socketInstance.off(item.event, item.handler);
  });
};

/* ===============================
   RECONNECT WITH LATEST TOKEN
================================ */
export const reconnectRealtimeSocket = () => {
  disconnectRealtimeSocket({
    clearListeners: true,
    clearAuth: false,
  });

  return connectRealtimeSocket();
};

/* ===============================
   GET INSTANCE
================================ */
export const getRealtimeSocket = () => socketInstance;

/* ===============================
   SOCKET STATUS
================================ */
export const isRealtimeConnected = () => {
  return Boolean(socketInstance?.connected);
};

export const isRealtimeConnecting = () => {
  return Boolean(isConnecting);
};

/* ===============================
   SAFE LISTENER HELPERS
================================ */
export const onRealtimeEvent = (eventName, handler) => {
  const safeEventName = String(eventName || "").trim();

  if (!safeEventName || typeof handler !== "function") {
    return () => {};
  }

  const socket = connectRealtimeSocket();

  if (!socket) return () => {};

  socket.off(safeEventName, handler);
  socket.on(safeEventName, handler);

  return () => {
    socket.off(safeEventName, handler);
  };
};

export const onceRealtimeEvent = (eventName, handler) => {
  const safeEventName = String(eventName || "").trim();

  if (!safeEventName || typeof handler !== "function") {
    return () => {};
  }

  const socket = connectRealtimeSocket();

  if (!socket) return () => {};

  socket.once(safeEventName, handler);

  return () => {
    socket.off(safeEventName, handler);
  };
};

/* ===============================
   GLOBAL APP REALTIME LISTENER
================================ */
export const onAppRealtimeUpdate = (handler) => {
  if (typeof handler !== "function") {
    return () => {};
  }

  const listener = (event) => {
    handler(event?.detail || {});
  };

  window.addEventListener("app-realtime-update", listener);

  return () => {
    window.removeEventListener("app-realtime-update", listener);
  };
};

/* ===============================
   SAFE EMIT FROM FRONTEND
================================ */
export const emitRealtimeEvent = (eventName, payload = {}) => {
  const safeEventName = String(eventName || "").trim();

  if (!safeEventName) return false;

  const socket = socketInstance || connectRealtimeSocket();

  if (!socket?.connected) {
    return false;
  }

  socket.emit(safeEventName, payload);
  return true;
};

/* ===============================
   HELPER EVENTS
================================ */
export const emitDnsRefreshRequest = (domainName = "") => {
  return emitRealtimeEvent("dns:refresh_request", {
    domainName: String(domainName || "").trim().toLowerCase(),
  });
};

export const emitDomainRefreshRequest = (domainName = "") => {
  return emitRealtimeEvent("domain:refresh_request", {
    domainName: String(domainName || "").trim().toLowerCase(),
  });
};

export const emitHostingRefreshRequest = () => {
  return emitRealtimeEvent("hosting:refresh_request", {});
};

export const emitHostingPanelRefreshRequest = () => {
  return emitRealtimeEvent("hosting_panel:refresh_request", {});
};

/* ===============================
   LOGOUT SOCKET CLEANUP
================================ */
export const logoutRealtimeSocket = () => {
  disconnectRealtimeSocket({
    clearListeners: true,
    clearAuth: true,
  });
};

/* ===============================
   DEBUG INFO
================================ */
export const getRealtimeDebugInfo = () => {
  const token = getToken();

  return {
    socketBase: SOCKET_BASE,
    apiBase: API_BASE,
    connected: Boolean(socketInstance?.connected),
    connecting: isConnecting,
    socketId: socketInstance?.id || null,
    hasToken: Boolean(token),
    tokenValid: token ? isTokenValid(token) : false,
    userKey: token ? getTokenUserKey(token) : "",
    activeUserKey: activeToken ? getTokenUserKey(activeToken) : "",
  };
};